import { CostLimitApi } from '@liks/lac-svc-api/esm/apis/CostLimitApi';
import type { CostLimit } from '@liks/lac-svc-api/esm/models/index';
import { ResponseError } from '@liks/lac-svc-api/esm/runtime';
import { lacApiConfiguration } from './lacApi';

class LacCostLimitService {
  private readonly costLimitApi = new CostLimitApi(lacApiConfiguration);

  async getCostLimit(tenantName: string): Promise<CostLimit | null> {
    const trimmedName = tenantName.trim();
    if (trimmedName === '') {
      return null;
    }

    try {
      return await this.costLimitApi.deLiksCostlimitBoundaryCostLimitsResourceReadCostLimit(
        { tenantName: trimmedName },
      );
    } catch (error) {
      if (error instanceof ResponseError && error.response.status === 404) {
        return null;
      }
      throw error;
    }
  }

  async listCostLimits(): Promise<CostLimit[]> {
    const limits: CostLimit[] = [];
    let offset = 0;
    const pageSize = 100;

    for (;;) {
      const page =
        await this.costLimitApi.deLiksCostlimitBoundaryCostLimitsResourceListCostLimits(
          {
            offset,
            limit: pageSize,
          },
        );

      limits.push(...page);

      if (page.length < pageSize) {
        break;
      }

      offset += pageSize;
    }

    return limits;
  }

  async saveCostLimit(
    tenantName: string,
    monthlyLimit: number | null,
    yearlyLimit: number | null,
  ): Promise<CostLimit> {
    const trimmedName = tenantName.trim();
    if (trimmedName === '') {
      throw new Error('Missing tenant name');
    }
    if (
      monthlyLimit != null &&
      (!Number.isFinite(monthlyLimit) || monthlyLimit <= 0)
    ) {
      throw new Error('Monthly cost limit must be greater than 0');
    }
    if (
      yearlyLimit != null &&
      (!Number.isFinite(yearlyLimit) || yearlyLimit <= 0)
    ) {
      throw new Error('Yearly cost limit must be greater than 0');
    }
    if (monthlyLimit == null && yearlyLimit == null) {
      throw new Error('At least one cost limit is required');
    }
    if (
      monthlyLimit != null &&
      yearlyLimit != null &&
      yearlyLimit > 12 * monthlyLimit
    ) {
      throw new Error(
        'Yearly cost limit must not exceed 12 times the monthly limit',
      );
    }

    const costLimit = {
      monthlyLimit: monthlyLimit ?? undefined,
      yearlyLimit: yearlyLimit ?? undefined,
    } as CostLimit;
    return this.costLimitApi.deLiksCostlimitBoundaryCostLimitsResourceSaveCostLimit(
      {
        tenantName: trimmedName,
        costLimit,
        body: costLimit,
      } as Parameters<
        CostLimitApi['deLiksCostlimitBoundaryCostLimitsResourceSaveCostLimit']
      >[0],
    );
  }
}

export const lacCostLimitService = new LacCostLimitService();
